import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Paper,
  Link as MuiLink,
} from "@mui/material";
import { DeleteOutline, WarningAmberRounded } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import axiosClient from "../../axiosClient";

const AccountDeletion = () => {
  const navigate = useNavigate();

  // --- State ---
  const [formData, setFormData] = useState({
    email: "", phone: "", card_number: "", dob: "", reason: "", confirm: false,
  });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [snackOpen, setSnackOpen] = useState(false);

  // --- Validations ---
  const isEmailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isPhoneValid = (p) => /^(09|\+639)\d{9}$/.test(p);
  const isReasonValid = (r) => r.trim().length >= 10;
  const isFormValid = isEmailValid(formData.email) && isPhoneValid(formData.phone) && isReasonValid(formData.reason) && formData.dob && formData.confirm;

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    // Only allow numbers for phone
    const finalVal = name === "phone" ? value.replace(/[^0-9+]/g, "") : val;
    setFormData({ ...formData, [name]: finalVal });
  };

  const handleBlur = (e) => setTouched({ ...touched, [e.target.name]: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setAlert({ type: "", message: "" });

    try {
      await axiosClient.post("/proxy/account-deletion", {
        email: formData.email,
        phone_number: formData.phone,
        card_no: formData.card_number || null,
        date_of_birth: formData.dob,
        reason: formData.reason,
      });

      setAlert({ type: "success", message: "Request submitted successfully." });
      setSnackOpen(true);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      setAlert({ type: "error", message: err?.response?.data?.message || "An error occurred." });
    } finally {
      setLoading(false);
    }
  };

  // Helper styles for modern inputs
  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px", 
      backgroundColor: "#fafafa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#b0b0b0" },
      "&.Mui-focused fieldset": { borderColor: "#ff6b6b" },
    }
  };

  return (
    <>
      <PageHeader title="ACCOUNT DELETION" pages={[]} />
      
      {/* Background Wrapper */}
      <Box sx={{ py: 10, display: "flex", justifyContent: "center", backgroundColor: "#f4f6f8", minHeight: "80vh" }}>
        <Container maxWidth="sm">
          
          {/* Card Container */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: "24px", 
              boxShadow: "0px 10px 40px rgba(0,0,0,0.04)", 
              backgroundColor: "#ffffff"
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                p: 1.5, 
                borderRadius: '50%', 
                backgroundColor: '#fff0f0', 
                mb: 2,
                color: '#ff6b6b'
              }}>
                <WarningAmberRounded sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1c23", mb: 1 }}>
                Delete Your Account
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.6 }}>
                We're sorry to see you go. Please verify your identity below to process your request.
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              
              <TextField 
                label="Email Address" name="email" fullWidth 
                onChange={handleChange} onBlur={handleBlur} 
                error={touched.email && !isEmailValid(formData.email)}
                sx={inputStyles}
              />
              
              <TextField 
                label="Phone Number" name="phone" fullWidth 
                value={formData.phone} onChange={handleChange} onBlur={handleBlur}
                error={touched.phone && !isPhoneValid(formData.phone)}
                sx={inputStyles}
              />
              
              {/* ✅ UPDATED: Added helperText */}
              <TextField 
                label="Card Number (Optional)" name="card_number" fullWidth 
                onChange={handleChange}
                helperText="Optional — but will help verify your identity faster"
                sx={inputStyles}
              />
              
              <TextField 
                label="Date of Birth" name="dob" type="date" fullWidth 
                InputLabelProps={{ shrink: true }} onChange={handleChange}
                sx={inputStyles}
              />
              
              <TextField 
                label="Reason for Deletion" name="reason" multiline rows={4} fullWidth 
                onChange={handleChange} onBlur={handleBlur}
                error={touched.reason && !isReasonValid(formData.reason)}
                helperText={touched.reason && !isReasonValid(formData.reason) ? "Minimum 10 characters required" : ""}
                sx={inputStyles}
              />

              {/* Confirmation Box */}
              <Box sx={{ 
                backgroundColor: "#fff5f5", 
                borderRadius: "16px", 
                p: 2, 
                border: "1px solid #ffe3e3" 
              }}>
                <FormControlLabel 
                  control={
                    <Checkbox 
                      name="confirm" 
                      onChange={handleChange} 
                      sx={{ 
                        color: "#ff6b6b", 
                        '&.Mui-checked': { color: "#ff4a4a" } 
                      }} 
                    />
                  } 
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#4a0f0f" }}>
                      I understand that this action is <strong>permanent</strong> and cannot be undone.
                    </Typography>
                  }
                />
              </Box>

              {alert.message && (
                <Alert severity={alert.type} sx={{ borderRadius: "12px" }}>
                  {alert.message}
                </Alert>
              )}

              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || !isFormValid} 
                startIcon={<DeleteOutline />} 
                sx={{ 
                  backgroundColor: "#ff6b6b", 
                  borderRadius: "50px", 
                  py: 1.8, 
                  fontSize: "1rem", 
                  fontWeight: 700,
                  boxShadow: "0 4px 14px 0 rgba(255, 107, 107, 0.39)",
                  "&:hover": { 
                    backgroundColor: "#ff4a4a",
                    boxShadow: "0 6px 20px 0 rgba(255, 107, 107, 0.23)"
                  },
                  textTransform: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Deletion Request"}
              </Button>

              <Box sx={{ textAlign: "center", mt: 1 }}>
                <MuiLink component={Link} to="/" underline="hover" sx={{ color: "#9ca3af", fontWeight: 500 }}>
                  Cancel and Return Home
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Snackbar open={snackOpen} autoHideDuration={3000} message="Request sent." />
    </>
  );
};

export default AccountDeletion;